import React, { useState } from "react";
import axios from "axios";
import {
  AIRTABLE_URL,
  AIRTABLE_URL_TECHNICIAN,
  AIRTABLE_HEADERS,
} from "./constants";
import { useNavigate } from "react-router-dom";

const DeleteAccount = () => {
  const [role, setRole] = useState("customer");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [pan, setPan] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [recoveredPassword, setRecoveredPassword] = useState("");

  const navigate = useNavigate();

  const handleDelete = async () => {
    if (role === "technician") {
      if (!phone || !password || !pan) {
        return setMessage("Please enter phone, password, and PAN to delete account.");
      }
    } else {
      if (!phone || !password) {
        return setMessage("Please enter your phone number and password.");
      }
    }

    setLoading(true);
    setMessage("");

    try {
      const deleteFromTable = async (url, isTechnician = false) => {
        const formula = isTechnician
          ? `AND({phone}='${phone}', {pan}='${pan}', {password}='${password}')`
          : `AND({phone}='${phone}', {password}='${password}')`;

        const res = await axios.get(
          `${url}?filterByFormula=${encodeURIComponent(formula)}`,
          { headers: AIRTABLE_HEADERS }
        );

        if (res.data.records.length === 0) return false;

        const actionPromises = res.data.records.map((record) => {
          const { id, fields } = record;

          if (isTechnician) {
            // ✅ Technician: Just clear phone and PAN
            return axios.patch(
              `${url}/${id}`,
              {
                fields: {
                  phone: "",
                  pan: "",
                },
              },
              {
                headers: AIRTABLE_HEADERS,
              }
            );
          } else {
            // ✅ Customer: Check orderId and paid
            const hasOrder = !!fields.orderId;
            const isPaid = fields.paid === true;

            if (hasOrder && !isPaid) {
              throw new Error("Customer account cannot be deleted until dues are paid.");
            }

            return axios.patch(
              `${url}/${id}`,
              {
                fields: {
                  phone: "",
                  address: "",
                  pincode: "",
                  state: "",
                  status: "account deleted",
                  deletedAt: new Date().toISOString(),
                },
              },
              {
                headers: AIRTABLE_HEADERS,
              }
            );
          }
        });

        await Promise.all(actionPromises);
        return true;
      };

      let deleted = false;

      if (role === "technician") {
        deleted = await deleteFromTable(AIRTABLE_URL_TECHNICIAN, true);
      } else {
        deleted = await deleteFromTable(AIRTABLE_URL);
      }

      if (deleted) {
        setMessage("✅ Your account has been deleted successfully.");
        localStorage.clear();
        setTimeout(() => navigate("/customer-login"), 2000);
      } else {
        setMessage("❌ No matching account found.");
      }
    } catch (err) {
      console.error(err);
      if (
        err.message.includes("Customer account cannot be deleted until dues are paid")
      ) {
        setMessage("❌ Please pay your due amount first before deleting your account.");
      } else {
        setMessage("Something went wrong. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!pan || !phone) {
      return setMessage("Please enter both phone number and PAN number.");
    }

    setLoading(true);

    try {
      const formula = `AND({phone}='${phone}', {pan}='${pan}')`;
      const res = await axios.get(
        `${AIRTABLE_URL_TECHNICIAN}?filterByFormula=${encodeURIComponent(formula)}`,
        { headers: AIRTABLE_HEADERS }
      );

      if (res.data.records.length === 0) {
        setMessage("❌ No technician found with this phone & PAN.");
      } else {
        const foundPassword = res.data.records[0].fields.password;
        setRecoveredPassword(foundPassword);
        setMessage("✅ Your password is: " + foundPassword);
      }
    } catch (err) {
      console.error(err);
      setMessage("Something went wrong during password recovery.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "90%",
    padding: "10px",
    marginBottom: "15px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "16px",
  };

  const buttonStyle = {
    backgroundColor: "#ff4d4d",
    color: "white",
    border: "none",
    padding: "12px",
    width: "90%",
    fontSize: "16px",
    borderRadius: "8px",
    cursor: "pointer",
  };

  return (
    <div style={{ padding: 20, maxWidth: 400, margin: "auto", textAlign: "center" }}>
      <h3>🗑️ Delete My Account</h3>

      <div style={{ marginBottom: 20 }}>
        <label>
          <input
            type="radio"
            checked={role === "customer"}
            onChange={() => setRole("customer")}
          />{" "}
          Customer
        </label>{" "}
        <label style={{ marginLeft: 20 }}>
          <input
            type="radio"
            checked={role === "technician"}
            onChange={() => setRole("technician")}
          />{" "}
          Technician
        </label>
      </div>

      <input
        type="tel"
        placeholder="Phone Number"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        style={inputStyle}
      />

      {role === "customer" && (
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle}
        />
      )}

      {role === "technician" && (
        <>
          <input
            type="tel"
            placeholder="PAN Number"
            value={pan}
            onChange={(e) => setPan(e.target.value)}
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </>
      )}

      <button onClick={handleDelete} style={buttonStyle} disabled={loading}>
        {loading ? "Deleting..." : "Delete My Account"}
      </button>

      {role === "technician" && (
        <>
          <p style={{ marginTop: 15, fontSize: 14 }}>
            Forgot your password?{" "}
            <button
              onClick={() => setForgotPasswordMode(!forgotPasswordMode)}
              style={{ color: "blue", background: "none", border: "none", cursor: "pointer" }}
            >
              Click here
            </button>
          </p>

          {forgotPasswordMode && (
            <>
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={inputStyle}
              />
              <input
                type="tel"
                placeholder="PAN Number"
                value={pan}
                onChange={(e) => setPan(e.target.value)}
                style={inputStyle}
              />
              <button onClick={handleForgotPassword} style={buttonStyle} disabled={loading}>
                {loading ? "Checking..." : "Recover Password"}
              </button>
              {recoveredPassword && (
                <p style={{ color: "green", marginTop: 10 }}>
                  Your password: <strong>{recoveredPassword}</strong>
                </p>
              )}
            </>
          )}
        </>
      )}

      {message && (
        <p style={{ marginTop: 15, color: message.includes("✅") ? "green" : "red" }}>
          {message}
        </p>
      )}
    </div>
  );
};

export default DeleteAccount;
